'use client';

import { memo, useMemo, type FC } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useNavigation } from '../../contexts/NavigationContext';
import { useReferralContestLeaderboard } from '../../queries/useReferralContestQueries';
import { ReferralContestLeaderboardResponseStatusEnum } from '../../services/generated/Api';
import { RotatingPromoArt } from '../RotatingPromoArt/RotatingPromoArt';
import { FaTrophy, RiArrowRightSLine } from '../icons';
import translations from './ReferralContestBanner.translations';

const KNIFE_SIZE = 160;

const ReferralContestBannerComponent: FC = () => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const { data } = useReferralContestLeaderboard();
  const board = data?.pages[0];

  const knifeSlides = useMemo(
    () =>
      (board?.prizes ?? [])
        .filter((prize) => prize.item?.imageUrl)
        .map((prize) => ({ key: prize.item!.hashName, imgProps: { src: prize.item!.imageUrl! } })),
    [board?.prizes],
  );

  const myRank = board?.me?.rank ?? null;

  if (!board || board.status === ReferralContestLeaderboardResponseStatusEnum.Upcoming) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => navigation.navigate('referral-contest')}
      className="relative flex w-full items-center gap-3 overflow-visible rounded-2xl bg-gradient-to-br from-amber-400 via-amber-500 to-orange-600 py-4 ps-[184px] pe-3 text-start shadow-lg shadow-black/30 transition active:opacity-95"
    >
      <span
        className="pointer-events-none absolute -top-10 start-2"
        style={{ height: KNIFE_SIZE, width: KNIFE_SIZE }}
      >
        <span className="absolute inset-x-4 bottom-4 top-10 rounded-full bg-white/25 blur-xl" aria-hidden />
        <RotatingPromoArt
          slides={knifeSlides}
          alt={t('prizeAlt')}
          size={KNIFE_SIZE}
          className="inset-0 drop-shadow-[0_6px_12px_rgba(0,0,0,0.45)]"
          fallback={<FaTrophy size="4em" className="text-white" />}
        />
      </span>

      <span className="relative min-w-0 flex-1">
        <span className="block text-base font-bold leading-tight text-white">{t('title')}</span>
        <span className="mt-0.5 block text-sm leading-snug text-white/90">

          {myRank ? <>{t('myPlace')} <bdi>#{myRank}</bdi></> : t('subtitle')}
        </span>
      </span>

      <RiArrowRightSLine size="1.5em" className="relative shrink-0 text-white/90 rtl:-scale-x-100" />
    </button>
  );
};

export const ReferralContestBanner = memo(ReferralContestBannerComponent);
